// ===========================================================================
// Telephone prompts (activity D6): write, draw, describe, in a chain.
//
// Everyone gets a sentence, draws it, passes the drawing on; the next person
// writes what they think it was; the next draws THAT. At the end the room
// plays the album back and watches "a cat teaching a class of ducks" become
// something else entirely. Everyone acts at once, so nobody is watched, which
// is why people who find rooms hard find this one easy. It needs four people
// for the chain to have anywhere to go.
//
// A prompt is a drawable scene: one or two subjects, one action, one twist.
// Animals doing people things are the reliable seam. Nothing here is a real
// person, a brand, or anything a 13-year-old should not draw. ASCII only.
// ===========================================================================

import { bandDeck, pickOne } from './pick.js';

/** Fewer than this and the chain cannot come back around. */
export const TELEPHONE_MIN_PLAYERS = 4;

const ADULT = Object.freeze([
  'a giraffe stuck in a revolving door',
  'an octopus juggling teacups',
  'a snowman on a beach holiday',
  'a penguin delivering pizza on a scooter',
  'a robot learning to ride a bicycle',
  'a dragon roasting marshmallows',
  'an elephant painting a self-portrait',
  'a wizard waiting for a bus in the rain',
  'a shark wearing a tiny hat',
  'a cow on a trampoline',
  'a sloth winning a race',
  'a bear ordering coffee',
  'a mouse steering a sailboat',
  'a frog playing a grand piano',
  'a dinosaur walking a tiny dog',
  'a chicken reading a newspaper on a train',
  'a whale in a bathtub',
  'a hedgehog carrying groceries',
  'a squirrel running a lemonade stand',
  'a goat climbing a lighthouse',
  'a moose in a hammock',
  'a fox knitting a very long scarf',
  'an astronaut watering plants on the moon',
  'a pirate afraid of a rubber duck',
  'a llama on a skateboard',
  'a crab conducting an orchestra',
  'a monkey fixing a leaky tap',
  'a pig flying a kite from a rooftop',
  'a turtle late for a train',
  'a rabbit painting a fence',
  'an owl doing a crossword',
  'a camel in a swimming pool',
  'a raccoon hosting a tea party',
  'a parrot teaching a dog to sing',
  'a hippo in a hot air balloon',
  'a lion afraid of a butterfly',
  'a snail delivering the mail',
  'a duck in a raincoat under a sunny sky',
  'a polar bear eating ice cream',
  'a kangaroo on a pogo stick',
  'a beaver building a treehouse',
  'a flamingo on a unicycle',
  'a walrus playing the trumpet',
  'a badger baking a giant cake',
  'a chef cooking on a volcano',
  'a queen walking a pet dinosaur',
  'a ghost doing the laundry',
  'a sheep with a very tall hairstyle',
  'a cactus wearing boots',
  'a spoon and a fork having an argument',
  'a house on chicken legs',
  'a cloud eating a sandwich',
  'a lighthouse with a mustache',
  'a toaster on a hiking trip',
  'a teapot going down a slide',
  'a tree on roller skates',
  'a snowman with a sunburn',
  'a sock looking for its partner in a desert',
  'a giant cupcake blocking traffic',
  'an umbrella flying a kite',
  'a mountain wearing a scarf',
  'the moon eating a slice of pizza',
  'a train made of bananas',
  'a bicycle with square wheels',
  'a fish riding a horse',
  'a tiny elephant in a teacup',
  'a jellyfish playing badminton',
  'a bee reading a map',
  'a panda on a seesaw with a mouse',
  'a caterpillar running a marathon',
  'a dolphin painting a rainbow',
  'a tortoise carrying a birthday cake',
  'a fox sketching a sunset',
  'a puppy driving a tractor',
  'an ant lifting a piano',
  'a rhino doing ballet',
  'a cat stuck in a bowl of spaghetti',
  'a horse wearing pajamas',
  'a chef juggling eggs on a tightrope',
  'a submarine full of goldfish',
  'a bear reading to a group of rabbits',
  'a snake tying a bow tie',
  'a sunflower holding an umbrella',
  'an octopus knitting eight socks',
  'a cow catching a frisbee',
  'a penguin playing chess with a seal',
  'a unicorn stuck in traffic',
  'a hamster piloting an airplane',
  'a scarecrow dancing in a cornfield',
  'a gorilla arranging flowers',
]);

const MINOR = Object.freeze([
  'a cat teaching a class of ducks',
  'a dog riding a skateboard down a hill',
  'a penguin eating spaghetti',
  'a dragon blowing bubbles',
  'a giraffe in a tiny car',
  'an elephant on a pogo stick',
  'a robot baking cookies',
  'a frog wearing a crown',
  'a monkey flying a kite',
  'a shark brushing its teeth',
  'a snowman at the beach',
  'a dinosaur playing basketball',
  'a hamster driving a bus',
  'a bear eating ice cream on a swing',
  'a unicorn painting a rainbow',
  'a turtle on a trampoline',
  'a pig in a hot air balloon',
  'a duck reading a book in a bathtub',
  'a rabbit playing the drums',
  'an octopus juggling apples',
  'a cow jumping over a fence',
  'a lion with a very long scarf',
  'a goldfish riding a bicycle',
  'a mouse eating a giant cheese',
  'a whale wearing sunglasses',
  'a parrot playing a guitar',
  'an owl doing a puzzle',
  'a squirrel with a backpack full of acorns',
  'a fox building a sandcastle',
  'a horse on roller skates',
  'a chicken in a spaceship',
  'a bee carrying a balloon',
  'a crab playing the piano',
  'a hippo in a tiny boat',
  'a kangaroo jumping over a rainbow',
  'a sloth in a race car',
  'a polar bear eating a popsicle',
  'a snail with a rocket on its shell',
  'a llama wearing rain boots',
  'a panda on a seesaw',
  'a raccoon having a picnic',
  'a wizard walking a pet cloud',
  'a ghost eating pancakes',
  'a pirate with a parrot made of candy',
  'an astronaut planting a flower on the moon',
  'a chef cooking on a mountain',
  'a superhero rescuing a kitten from a tree',
  'a mermaid playing volleyball',
  'a fairy fixing a bicycle',
  'a giant eating a tiny sandwich',
  'a house with wings',
  'a car made of cheese',
  'a tree with a face and a hat',
  'a cloud raining candy',
  'the sun wearing sunglasses',
  'the moon eating a cookie',
  'a banana on a skateboard',
  'a pizza with legs running away',
  'a teapot on a swing',
  'a sock puppet giving a speech',
  'a toaster on the beach',
  'a spoon and a fork dancing',
  'a pencil drawing itself',
  'a book flying like a bird',
  'a bicycle with balloon wheels',
  'a train made of pancakes',
  'a boat sailing on a rainbow',
  'a castle made of ice cream',
  'a treehouse with a slide into a pool',
  'a rocket shaped like a carrot',
  'a snowman with a flower hat',
  'a very tall stack of pancakes with a cat on top',
  'a puppy in a birthday hat',
  'a kitten chasing a butterfly through a garden',
  'a dolphin jumping through a hula hoop',
  'a bird building a nest out of spaghetti',
  'a caterpillar wearing lots of shoes',
  'a cactus hugging a balloon',
  'a flamingo standing on a basketball',
  'a tiger playing hide and seek behind a tiny tree',
  'an ant carrying a birthday cake',
  'a zebra painting its own stripes',
  'a koala napping on a rocket',
  'a goat on a mountain of pillows',
  'a jellyfish holding an umbrella',
  'a beaver building a slide',
  'a walrus playing the tuba',
  'a hedgehog rolling down a hill with a balloon',
  'a seal juggling snowballs',
  'a puppy and a duck sharing an umbrella',
]);

export const TELEPHONE_PROMPTS = Object.freeze({ adult: ADULT, minor: MINOR });

/**
 * One prompt the room has not seen, or any prompt once it has seen them all.
 * @param {'adult'|'minor'} band
 * @param {Set<string>} [excludeSet]
 * @param {() => number} [rnd]
 */
export function pickTelephonePrompt(band, excludeSet = new Set(), rnd = Math.random) {
  return pickOne(bandDeck(TELEPHONE_PROMPTS, band), excludeSet, rnd);
}

export default { TELEPHONE_PROMPTS, TELEPHONE_MIN_PLAYERS, pickTelephonePrompt };
